import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Collapse,
  IconButton,
  Tooltip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useTranslation } from 'react-i18next';

const PreviewRow = ({ row, entityType }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const getStatusIcon = () => {
    switch (row.status) {
      case 'will_import':
        return <CheckCircleIcon color="success" fontSize="small" />;
      case 'will_update':
        return <EditIcon color="info" fontSize="small" />;
      case 'will_skip':
        return <WarningIcon color="warning" fontSize="small" />;
      default:
        return <ErrorIcon color="error" fontSize="small" />;
    }
  };

  const getStatusChip = () => {
    switch (row.status) {
      case 'will_import':
        return <Chip size="small" label={t('Will import')} color="success" variant="outlined" />;
      case 'will_update':
        return <Chip size="small" label={t('Will update')} color="info" variant="outlined" />;
      case 'will_skip':
        return <Chip size="small" label={t('Will skip')} color="warning" variant="outlined" />;
      default:
        return <Chip size="small" label={t('Error')} color="error" variant="outlined" />;
    }
  };

  // Get display name from mapped data
  const getDisplayName = () => {
    const mapped = row.mapped || {};
    if (entityType === 'customer') {
      return mapped.name || mapped.company_name || `${mapped.first_name || ''} ${mapped.last_name || ''}`.trim() || '-';
    }
    return mapped.name || '-';
  };

  // Get display email/identifier
  const getDisplayIdentifier = () => {
    const mapped = row.mapped || {};
    if (entityType === 'customer') {
      return mapped.business_id || mapped.email || '-';
    }
    if (entityType === 'job') {
      return mapped.job_code || '-';
    }
    return mapped.manufacturer_code || '-';
  };

  // Get additional info
  const getAdditionalInfo = () => {
    const mapped = row.mapped || {};
    if (entityType === 'customer') {
      return mapped.email || mapped.phone || '-';
    }
    return mapped.price ? `${mapped.price} EUR` : '-';
  };

  return (
    <>
      <TableRow
        sx={{
          '&:hover': { bgcolor: 'action.hover' },
          ...(row.status === 'will_skip' && { bgcolor: 'warning.50' }),
        }}
      >
        {/* Expand button */}
        <TableCell padding="checkbox">
          <IconButton
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>

        {/* Status */}
        <TableCell>
          {getStatusIcon()}
        </TableCell>

        {/* Row number */}
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {row.row_num}
          </Typography>
        </TableCell>

        {/* Name */}
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {getDisplayName()}
          </Typography>
        </TableCell>

        {/* Identifier */}
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {getDisplayIdentifier()}
          </Typography>
        </TableCell>

        {/* Additional info */}
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {getAdditionalInfo()}
          </Typography>
        </TableCell>

        {/* Status chip */}
        <TableCell>
          {getStatusChip()}
          {row.message && (
            <Tooltip title={row.message}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {row.message}
              </Typography>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>

      {/* Expanded details */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, px: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('Mapped data')}:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {Object.entries(row.mapped || {}).map(([key, value]) => (
                  <Chip
                    key={key}
                    size="small"
                    label={`${key}: ${value}`}
                    variant="outlined"
                    sx={{ maxWidth: 300 }}
                  />
                ))}
              </Box>

              {row.message && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('Message')}:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {row.message}
                  </Typography>
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const ImportPreviewTable = ({ previewData, entityType }) => {
  const { t } = useTranslation();

  if (!previewData || previewData.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="text.secondary">
          {t('No preview data available')}
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'background.default' }}>
            <TableCell padding="checkbox" />
            <TableCell sx={{ width: 50 }}></TableCell>
            <TableCell sx={{ fontWeight: 600, width: 60 }}>{t('Row')}</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {entityType === 'customer' ? t('Name') : entityType === 'job' ? t('Job name') : t('Part name')}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {entityType === 'customer' ? t('Business ID / Email') : entityType === 'job' ? t('Job code') : t('Code')}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {entityType === 'customer' ? t('Contact') : t('Price')}
            </TableCell>
            <TableCell sx={{ fontWeight: 600, width: 180 }}>{t('Status')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {previewData.map((row, index) => (
            <PreviewRow key={index} row={row} entityType={entityType} />
          ))}
        </TableBody>
      </Table>

      {/* Info about limited preview */}
      {previewData.length >= 10 && (
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
          <Typography variant="body2" color="text.secondary">
            {t('Showing first {{count}} rows', { count: previewData.length })}
          </Typography>
        </Box>
      )}
    </TableContainer>
  );
};

export default ImportPreviewTable;
